import { useCallback } from 'react'
import { useInterviewStore, InterviewStage } from '@/store/interviewStore'

// Hook for coding stage with problemId
export function useStageState(stage: 'coding', problemId: string): {
  data: { code: string; language: string | null; boilerplate: string; isDirty: boolean; lastSaved: string | null }
  updateData: (code: string) => void
}

// Hook for database-questions stage with problemId
export function useStageState(stage: 'database-questions', problemId: string): {
  data: { query: string; isDirty: boolean; lastSaved: string | null }
  updateData: (query: string) => void
}

// Hook for other stages
export function useStageState(stage: Exclude<InterviewStage, 'coding' | 'database-questions' | 'guidelines' | 'completed'>): {
  data: { content: any; isDirty: boolean; lastSaved: string | null }
  updateData: (content: any) => void
}

export function useStageState(stage: InterviewStage, problemId?: string) {
  const store = useInterviewStore()

  const getData = useCallback(() => {
    if (stage === 'coding' && problemId) {
      return store.getCodingData(problemId)
    }

    if (stage === 'database-questions' && problemId) {
      return store.getDatabaseData(problemId)
    }

    switch (stage) {
      case 'system-design':
        return store.getSystemDesignData()
      case 'whiteboard':
        return store.getWhiteboardData()
      case 'data-modeling':
        return store.getDataModelingData()
      case 'uml':
        return store.getUMLData()
      case 'notes':
      case 'solution-design':
      case 'code-review':
      case 'video-interview':
      case 'pair-programming':
        return store.getStageData(stage)
      default:
        return { content: '', isDirty: false, lastSaved: null }
    }
  }, [stage, problemId, store])

  const updateData = useCallback((value: any) => {
    if (stage === 'coding' && problemId) {
      store.updateCodingData(problemId, value)
      return
    }

    if (stage === 'database-questions' && problemId) {
      store.updateDatabaseData(problemId, value)
      return
    }

    switch (stage) {
      case 'system-design':
        store.updateSystemDesignContent(value)
        break
      case 'whiteboard':
        store.updateWhiteboardContent(value)
        break
      case 'data-modeling':
        store.updateDataModelingContent(value)
        break
      case 'uml':
        store.updateUMLContent(value)
        break
      case 'notes':
      case 'solution-design':
      case 'code-review':
      case 'video-interview':
      case 'pair-programming':
        store.updateStageData(stage, value)
        break
      default:
        break
    }
  }, [stage, problemId, store])

  return {
    data: getData(),
    updateData
  }
}

// Navigation hook
export function useStageNavigation() {
  const store = useInterviewStore()
  
  const switchStage = useCallback((newStage: InterviewStage) => {
    // Navigation logic will handle saving if needed
    store.setActiveStage(newStage)
    
    // Clear problem ID when leaving coding
    if (newStage !== 'coding') {
      store.setActiveProblemId(null)
    }
  }, [store])

  const switchProblem = useCallback((problemId: string) => {
    // Set active stage to coding if not already
    if (store.activeStage !== 'coding') {
      store.setActiveStage('coding')
    }
    store.setActiveProblemId(problemId)
  }, [store])

  return {
    activeStage: store.activeStage,
    activeProblemId: store.activeProblemId,
    switchStage,
    switchProblem
  }
}
